/* GASSISA
 * =========================================================================================
 * Descripcion: Layout del ticket de ventas. Resuelve el diseno guardado
 *              (ticketLayout de Parametros) a las secciones que consume la
 *              impresion. La X es absoluta; la Y solo se usa para agrupar
 *              objetos que comparten renglon en el disenador.
 *
 *              La impresion no recorta, no envuelve y no aplica ancho de campo.
 *              El unico limite es el de transporte (96 caracteres por linea),
 *              que se resuelve al emitir los bytes; el ancho fisico lo ajusta
 *              la impresora.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 20/10/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion
 *      2       | 21/10/2026 | Lic. Jose Luis Aragon Cervantes  | Sin columnas ni recorte
 * ---------------------------------------------------------------------------------------*/

import {
    CAMPOS_DATOS,
    CAMPOS_DETALLE,
    CAMPOS_TOTALES,
    normalizarObjetoDetalle,
} from "./ticketLayoutCatalogs";
import {
    SECCION_DEFAULT,
    AGRUPACION_Y_MM,
    MAX_COLUMNAS_LINEA,
    CALIBRACION_POR_DEFECTO,
    altoLineaMm,
} from "./ticketPerfil";

const normalizarObjetos = (seccion, campos) => (seccion?.objetos || []).map((o, i) => normalizarObjetoDetalle(o, i, campos));

export const resolveTicketLayout = (parametrosCajaForm) => {
    const layout = parametrosCajaForm?.ticketLayout || {};
    const normalizarSeccion = (seccion) => ({ ...SECCION_DEFAULT, ...(seccion || {}) });
    return {
        columnas: Math.max(8, Math.round(Number(layout?.columnas ?? MAX_COLUMNAS_LINEA) || MAX_COLUMNAS_LINEA)),
        calibracion: { ...CALIBRACION_POR_DEFECTO, ...(layout?.calibracion || {}) },
        logo: {
            ancho: Number(layout?.logo?.ancho ?? 50) || 0,
            alto: Number(layout?.logo?.alto ?? 0) || 0,
        },
        cabecero: normalizarSeccion(layout?.cabecero),
        pie: normalizarSeccion(layout?.pie),
        datos: { objetos: normalizarObjetos(layout?.datos, CAMPOS_DATOS) },
        detalle: { objetos: normalizarObjetos(layout?.detalle, CAMPOS_DETALLE) },
        totales: { objetos: normalizarObjetos(layout?.totales, CAMPOS_TOTALES) },
    };
};

export const layoutTieneObjetos = (parametrosCajaForm) => {
    // Un valor heredado de ParametrosCaja nunca debe activar el diseñador.
    // Sólo la lectura satisfactoria de TICKET_LAYOUT_{caja} en Parametros lo habilita.
    if (parametrosCajaForm?.ticketLayoutRegistrado !== true) return false;
    const layout = parametrosCajaForm?.ticketLayout || {};
    return [layout?.datos, layout?.detalle, layout?.totales].some((seccion) => (seccion?.objetos || []).length > 0);
};

// Agrupa objetos que comparten renglon (mismo Y dentro de la tolerancia).
export const agruparObjetosPorRenglon = (objetos) => {
    const grupos = [];
    [...objetos].sort((a, b) => (a.yMm - b.yMm) || (a.xMm - b.xMm)).forEach((objeto) => {
        const grupo = grupos.find((g) => Math.abs(g.yMm - objeto.yMm) <= AGRUPACION_Y_MM);
        if (grupo) grupo.objetos.push(objeto);
        else grupos.push({ yMm: objeto.yMm, objetos: [objeto] });
    });
    grupos.forEach((grupo) => grupo.objetos.sort((a, b) => a.xMm - b.xMm));
    return grupos.sort((a, b) => a.yMm - b.yMm);
};

// Texto completo del objeto: solo trim y saltos de linea explicitos.
export const envolverObjeto = (objeto, texto) => {
    const textoLimpio = String(texto ?? "")
        .split("\n")
        .map((linea) => linea.trim())
        .join("\n")
        .trim();
    const lineas = textoLimpio.length > 0 ? textoLimpio.split("\n") : [];

    return {
        lineas,
        columnas: 0,
        columnasCaja: 0,
        columnasBorde: 0,
        lineasEnvueltas: lineas.length,
        lineasPermitidas: lineas.length,
        lineasReservadas: lineas.length,
        autoAlto: true,
        sinTopeVertical: true,
        truncado: false,
    };
};

// Plan visual del disenador. Conserva la Y para dibujar las filas, pero la
// impresion no usa esas coordenadas: solo los saltos de linea.
export const construirPlanSeccion = ({
    objetos,
    textoDe,
    calibracion = CALIBRACION_POR_DEFECTO,
}) => {
    const grupos = agruparObjetosPorRenglon(objetos);
    const filas = [];
    const altoLinea = altoLineaMm(calibracion);
    let finAnterior = 0;

    grupos.forEach((grupo) => {
        const items = grupo.objetos.map((objeto) => {
            const texto = String(objeto.tipo === "campo" ? textoDe(objeto.campo, objeto) : (objeto.texto ?? ""));
            return { objeto, texto, ...envolverObjeto(objeto, texto) };
        });

        const lineasEfectivas = Math.max(1, ...items.map((item) => item.lineas.length));
        const yAnclaLineas = Math.max(0, Math.round((Number(grupo.yMm) || 0) / altoLinea));
        const yLineas = Math.max(yAnclaLineas, finAnterior);
        finAnterior = yLineas + lineasEfectivas;

        filas.push({
            yLineas,
            yMm: yLineas * altoLinea,
            yAnclaLineas,
            altoReservadoLineas: lineasEfectivas,
            columnas: 0,
            items,
        });
    });

    const altoTotalLineas = filas.reduce(
        (maximo, fila) => Math.max(maximo, fila.yLineas + fila.altoReservadoLineas),
        0
    );

    return {
        filas,
        altoTotalLineas,
        altoTotalMm: altoTotalLineas * altoLinea,
        columnasMax: 0,
        unidadesLinea: calibracion?.unidadesLinea ?? 24,
        calibracion,
        avisos: [],
    };
};
